import {
  Avatar,
  Button,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import moment from 'moment'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import './LanguageButton.scss'
import { LocalizedTypography } from './LocalizedTypography'
import SupportedLanguages from './SupportedLanguages.json'

export const LanguageButton = (props: {
  className?: string
  variant?: 'button' | 'menu-item'
}) => {
  const { i18n } = useTranslation()
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const btnRef = React.useRef(undefined)

  const languageData =
    SupportedLanguages.ui_languages[i18n.language] ||
    SupportedLanguages.ui_languages[i18n.language.split('-')[0]] ||
    SupportedLanguages.ui_languages.en

  const LanguageMenu = useCallback(() => {
    const changeLanguage = (
      e: React.MouseEvent<HTMLLIElement>,
      language: string
    ) => {
      setMenuOpen(false)

      e.bubbles = false
      e.preventDefault()

      setLoading(true)

      i18n.changeLanguage(language, () => {
        moment.locale(language)
        setLoading(false)
        setMenuOpen(false)
      })

      const fix = language.split('-')[0]
        ? language.split('-')[0].toUpperCase()
        : 'en'

      window['Beamer']?.update({
        language: fix,
      })
    }

    return (
      <Menu
        className="language-button-menu"
        anchorEl={btnRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      >
        <div className="language-button-menu--title">
          <LocalizedTypography
            translationKey={'common:language'}
            translationDefaultValue={'language'}
            variant="h6"
          />
        </div>
        {Object.keys(SupportedLanguages.ui_languages)?.map((langKey) => {
          return (
            <MenuItem
              key={langKey}
              onClick={(e) => {
                e.bubbles = false
                e.preventDefault()
                changeLanguage(e, langKey)
              }}
              selected={langKey === languageData?.lang_code}
            >
              <ListItemAvatar>
                <Avatar
                  src={SupportedLanguages.ui_languages[langKey].flag}
                  alt={langKey}
                  style={{
                    height: '24px',
                    width: '24px',
                  }}
                />
              </ListItemAvatar>
              <ListItemText
                primary={SupportedLanguages.ui_languages[langKey].description}
              />
            </MenuItem>
          )
        })}
      </Menu>
    )
  }, [i18n, languageData?.lang_code, menuOpen])

  if (props.variant === 'menu-item') {
    return (
      <>
        <MenuItem
          onClick={(e) => {
            e.bubbles = false
            e.preventDefault()
            setMenuOpen(() => true)
          }}
          ref={btnRef}
        >
          <ListItemAvatar>
            {loading ? (
              <Skeleton
                variant="circle"
                style={{ width: '1.2rem', height: '1.2rem' }}
              />
            ) : (
              <Avatar
                src={languageData?.flag}
                style={{ width: '1em', height: '1em' }}
              />
            )}
          </ListItemAvatar>
          <ListItemText primary={languageData?.description} />
        </MenuItem>
        <LanguageMenu />
      </>
    )
  }

  return (
    <>
      <Button variant="text" onClick={() => setMenuOpen(true)} ref={btnRef}>
        {loading ? (
          <Skeleton variant="circle" style={{ width: '1em', height: '1em' }} />
        ) : (
          <Avatar
            src={languageData?.flag}
            style={{ width: '1em', height: '1em', marginRight: '.5em' }}
          />
        )}
        {loading ? (
          <Skeleton variant="text" />
        ) : (
          <ListItemText primary={languageData?.description} />
        )}
      </Button>
      <LanguageMenu />
    </>
  )
}
